import React, { useState, useEffect } from 'react'
import { Container } from 'pcln-design-system'
import fetch from 'isomorphic-unfetch'
import { OptInBody, OptInText, SignUpComplete } from '@pcln/email-signup'
import {
  useContentful,
  getVariantContent,
  ContentfulRichText,
  IVariationCollectionType,
  IOptInType
} from '@pcln/contentful-components'
import styled from 'styled-components'
import analytics from '@/shared-utils/analytics'
import useSeti from '@/hooks/useSeti'
import isContentIVariationCollectionType from '@/shared-utils/is-content-ivariation-collection-type'
import useLocalStorageToTrackOptin from './customHooks/useLocalStorageToTrackOptin'
import CONTENT from './content'

type EmailSignupProps = {
  closeToast?: () => void
  setiVariantHasContent?: (arg: boolean) => void
  isToast?: boolean
  sourceId: string
  isToastVisibile?: boolean
  category?: string
}

const getContentTypeId = (isToast: boolean) => {
  return isToast ? 'emailOptInToastModule' : 'emailOptIn'
}

function mapContentfulContent(optIn: IOptInType) {
  const { url, label, analyticsTracking } = optIn.callToAction ?? {}
  const { placeholder, error } = optIn?.input?.[0] ?? {}
  const {
    headline,
    subheadline,
    successHeadline,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTracking
  } = optIn
  return {
    headline,
    subheadline,
    successHeadline,
    placeholder,
    invalidEmailError: new Error(error),
    buttonText: label,
    url,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTrackingOnView: {
      category: googleAnalyticsTracking?.category || 'email opt-in',
      action: googleAnalyticsTracking?.action,
      label: googleAnalyticsTracking?.label
    },
    googleAnalyticsTrackingOnClick: {
      category: analyticsTracking?.category,
      action: analyticsTracking?.action,
      label: analyticsTracking?.label
    }
  }
}

const RichText = styled(ContentfulRichText)`
  margin: 0px;
`

export default function EmailSignUpComponent({
  closeToast,
  isToast = false,
  sourceId,
  isToastVisibile = false,
  setiVariantHasContent,
  category = ''
}: EmailSignupProps) {
  const optInFromContentful = useContentful(getContentTypeId(isToast)) as
    | IOptInType
    | IVariationCollectionType
    | undefined

  const isVariantCollection =
    isContentIVariationCollectionType<IOptInType>(optInFromContentful)

  const setiName = useSeti(
    isVariantCollection ? optInFromContentful.experimentName : ''
  )

  const optIn = isVariantCollection
    ? (getVariantContent(setiName, optInFromContentful) as
        | IOptInType
        | undefined)
    : optInFromContentful

  const hasContentfulData = optIn != null

  useEffect(() => {
    if (setiVariantHasContent) setiVariantHasContent(hasContentfulData)
  }, [hasContentfulData, setiVariantHasContent])

  const {
    headline,
    subheadline,
    successHeadline,
    placeholder,
    invalidEmailError,
    buttonText,
    url,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTrackingOnView,
    googleAnalyticsTrackingOnClick
  } = hasContentfulData ? mapContentfulContent(optIn) : CONTENT

  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [emailEntered, setEmailEntered] = useState<string>('')
  const [fireAnalyticsOnce, setFireAnalyticsOnce] = useState(false)
  const { onSuccess } = useLocalStorageToTrackOptin()

  const submitEmail = async ({
    emailAddress
  }: {
    emailAddress: string
  }): Promise<void> => {
    setIsLoading(true)
    setEmailEntered(emailAddress)
    const submission = {
      alertSubscribeList: [
        {
          emailAddress,
          sourceId,
          alertId,
          channelType,
          contactSourceType,
          partnerCode: 'PCLN'
        }
      ]
    }

    try {
      const response = await fetch(String(url), {
        method: 'POST',
        body: JSON.stringify(submission),
        headers: { 'content-type': 'application/json' }
      })

      if (response.ok) {
        const body = await response.json()
        if (body.success) {
          onSuccess()
          setSuccess(true)
          analytics.fireEvent({
            action: googleAnalyticsTrackingOnClick.action || 'Email Confirm',
            category: googleAnalyticsTrackingOnClick.category || category,
            label: googleAnalyticsTrackingOnClick.label || 'Thank You'
          })
        } else {
          throw new Error(body.errorDesc || 'Email registration failure')
        }
      } else {
        throw new Error(response.statusText || 'Email registration failure')
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err)
      }
    }
    setIsLoading(false)
  }

  useEffect(() => {
    const fireAnalyticsOnView = () => {
      if (googleAnalyticsTrackingOnView?.action) {
        analytics.fireEvent({
          action: googleAnalyticsTrackingOnView.action,
          category: googleAnalyticsTrackingOnView.category,
          label: googleAnalyticsTrackingOnView.label
        })
        setFireAnalyticsOnce(true)
      }
    }

    if (!fireAnalyticsOnce) {
      if (isToast) {
        if (isToastVisibile) fireAnalyticsOnView()
      } else {
        fireAnalyticsOnView()
      }
    }
  }, [
    googleAnalyticsTrackingOnView.action,
    googleAnalyticsTrackingOnView.category,
    googleAnalyticsTrackingOnView.label,
    isToast,
    isToastVisibile,
    fireAnalyticsOnce
  ])

  return (
    <Container maxWidth={1080}>
      <OptInBody
        success={success}
        apiErrorMessage={error}
        invaildEmailErrorMessage={invalidEmailError}
        isLoading={isLoading}
        submitEmail={email => submitEmail(email)}
        buttonText={buttonText}
        placeholder={placeholder}
        borderRadius="lg"
        formControlSize="large"
        successTemplate={
          <SignUpComplete
            headline={<RichText body={successHeadline} />}
            subheadline={emailEntered}
            closeToast={closeToast}
            isCloseButtonShown={!!closeToast}
          />
        }
      >
        <OptInText
          subheadlineFontSize={2}
          headline={<RichText body={headline} />}
          subheadline={<RichText body={subheadline} />}
          spaceBetweenHeadlines={1}
        />
      </OptInBody>
    </Container>
  )
}
